import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { useGetArticleMetricsHook } from 'api/queries/articles/articles';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { ArticleMetricsModel } from 'api/models';

const ARTICLE_METRICS_QUERY_KEY = 'placed-article-metrics';

export const usePlacedArticleMetrics = (additionalArticleNumbers: string[] = []) => {
    const getArticleMetrics = useGetArticleMetricsHook();
    const articles = usePlacementStore(useShallow((state) => state.articles));

    const placedArticleNumbers = useMemo(() => {
        const articleNumbers = new Set<string>();

        Object.values(articles).forEach((article) => {
            const instanceCount = Object.keys(article.articleInstances).length;
            if (instanceCount === 0 || !article.articleNumber) {
                return;
            }

            articleNumbers.add(article.articleNumber);
        });

        additionalArticleNumbers.forEach((articleNumber) => articleNumbers.add(articleNumber));

        return Array.from(articleNumbers).sort();
    }, [additionalArticleNumbers, articles]);

    const { data, isPending, isFetching } = useQuery<ArticleMetricsModel[]>({
        queryKey: [ARTICLE_METRICS_QUERY_KEY, placedArticleNumbers],
        queryFn: async () => {
            const response = await getArticleMetrics(placedArticleNumbers, { liveData: true });
            return response.data?.metrics ?? [];
        },
        enabled: placedArticleNumbers.length > 0,
        staleTime: 60 * 1000
    });

    const metricsByArticleNumber = useMemo(() => {
        const map = new Map<string, ArticleMetricsModel>();

        (data ?? []).forEach((metric) => {
            if (metric.articleNumber) {
                map.set(metric.articleNumber, metric);
            }
        });

        return map;
    }, [data]);

    return {
        metricsByArticleNumber,
        isPending,
        isFetching
    };
};
